import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import { debounce } from 'lodash';
import { Control, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { SHARE_EXCHANGE_STAGES_OPTIONS } from 'constants/deals';
import { INVESTORS_TYPE_OPTIONS } from 'constants/investors';
import { INVOICE_STATUS_OPTIONS } from 'constants/invoices';
import { addPaddingToTd, CREATE_CUSTOM_MAIL_FORM_FIELDS } from 'constants/mailing';
import { FieldTypes } from 'constants/shared';
import { ButtonVariants } from 'constants/shared/button';
import useFilterParameters from 'hooks/use-filter-parameters/useFilterParameters';
import useModal from 'hooks/use-modal/useModal';
import { usePaginationWithSearch } from 'hooks/use-pagination-with-search/usePaginationWithSearch';
import { Option } from 'interfaces';
import { getCustomMailEntityMembers, getCustomMailUsers } from 'modules/custom-mail/action';
import {
  selectMailEntities,
  selectEntityMembersList,
  selectMailTempalate,
  selectMailUsersList,
  selectCountryList,
} from 'modules/custom-mail/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import MailingConfirmationModal from 'page-components/custom-mail/MailingConfirmationModal';
import {
  ButtonWithTooltip,
  CreatableSelectFormInput,
  FormContent,
  Input,
  SelectFormInput,
  SnippetLayout,
} from 'shared-components';
import ExternalMultiSelect from 'shared-components/ExternalMultiSelect';
import { getUserFiltersQuery } from 'utils';

const SHARED_TEMPLATE_CLASSNAMES =
  '[&_a]:text-blue-600 bg-violet-100  h-[300px] overflow-y-auto text-grey-800 p-0 mt-6';

interface SelectedOption {
  id: number;
  name: string;
}

const MailingForm = () => {
  const dispatch = useAppDispatch();
  const entities = useSelector(selectMailEntities);
  const { preview } = useSelector(selectMailTempalate);
  const content = '...';
  const { page } = usePaginationWithSearch();
  const { list: entityMembers } = useAppSelector(selectEntityMembersList);
  const { list: countryList } = useAppSelector(selectCountryList);
  const { list: usersList } = useAppSelector(selectMailUsersList);
  const { filter } = useFilterParameters();
  const [template, setTemplate] = useState(content);
  const [shouldBeCleared, setShouldBeCleared] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const [fixedPreview, setFixedPreview] = useState(preview);
  const { isOpen, onOpen, onClose } = useModal();
  const [entityMembersFilters, setEntityMembersFilters] = useState('');
  const [usersFilters, setUsersFilters] = useState('');

  const [usersAreLoading, setUsersAreLoading] = useState(false);
  const [entityMembersAreLoading, setEntityMembersAreLoading] = useState(false);

  useEffect(() => {
    const newHtml = addPaddingToTd(preview);
    setFixedPreview(newHtml);
  }, [preview]);

  const {
    register,
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subject: 'New message from PlanD',
      entity: null,
      entityMember: null,
      investorType: null,
      country: null,
      dealStage: null,
      invoiceStatus: null,
      lessThan: null,
      greaterThan: null,
      users: null,
    },
    mode: 'onSubmit',
  });

  const selectedEntity = watch('entity', null) as Option | null;
  const selectedEntityMember = watch('entityMember', null) as Option[] | null;
  const selectedUsers = watch('users', null) as Option[] | null;
  const selectedInvestorType = watch('investorType', null) as Option | null;
  const selectedDealStage = watch('dealStage', null) as Option | null;
  const selectedInvoiceStatus = watch('invoiceStatus', null) as Option | null;
  const selectedCountry = watch('country', null) as Option | null;
  const selectedLessThan = watch('lessThan', null);
  const selectedGreaterThan = watch('greaterThan', null);
  const setSubject = watch('subject', '');

  const [selectedResults, setSelectedResults] = useState({
    deals: [] as SelectedOption[],
    companies: [] as SelectedOption[],
    users: [] as SelectedOption[],
  });

  const [usersQuery, setUsersQuery] = useState<string>('');

  const loadFilteredUsers = useCallback(
    debounce((entityValue: string, page: number, urlFilters = '', extraFilters = '') => {
      setUsersAreLoading(true);
      setUsersFilters(`${urlFilters}&${extraFilters}`);
      dispatch(
        getCustomMailUsers({
          page,
          per_page: 20,
          entity: entityValue,
          filter: `${urlFilters}&${extraFilters}`.replace(/^&|&$/g, ''),
        }),
      )
        .unwrap()
        .finally(() => setUsersAreLoading(false));
    }, 1500),
    [dispatch],
  );

  useEffect(() => {
    if (selectedEntityMember && selectedEntity) {
      const selectedData = selectedEntityMember.map((option) => ({
        id: parseInt(option.value, 10),
        name: option.label,
      }));
      if (selectedEntity.value === 'deal') {
        setSelectedResults((prev) => ({ ...prev, deals: selectedData }));
      } else if (
        selectedEntity.value === 'company' ||
        selectedEntity.value === 'investor' ||
        selectedEntity.value === 'founder'
      ) {
        setSelectedResults((prev) => ({ ...prev, companies: selectedData }));
      }
    }
  }, [selectedEntityMember, selectedEntity]);

  useEffect(() => {
    if (
      selectedUsers &&
      selectedEntity &&
      (selectedEntity.value === 'company' ||
        selectedEntity.value === 'deal' ||
        selectedEntity.value === 'investor' ||
        selectedEntity.value === 'founder' ||
        selectedEntity.value === 'invoices')
    ) {
      const selectedData = selectedUsers.map((option) => ({
        id: parseInt(option.value, 10),
        name: option.label,
      }));
      setSelectedResults((prev) => ({ ...prev, users: selectedData }));
    }
  }, [selectedUsers, selectedEntity]);

  const inputLabel = useMemo(() => {
    if (!selectedEntity) return 'Select Recipients';
    switch (selectedEntity.value) {
      case 'deal':
        return 'Deals';
      case 'company':
        return 'Companies';
      case 'investor':
        return 'Companies';
      case 'founder':
        return 'Companies';
      default:
        return 'Select Recipients';
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (!selectedEntity) return;
    const extraFilters = getUserFiltersQuery({
      company_id: selectedResults.companies.map((c) => c.id).join(',') || undefined,
      deal_parameter_id: selectedResults.deals.map((d) => d.id).join(',') || undefined,
      deal_stage: selectedDealStage?.value,
      user_type:
        selectedEntity?.value === 'investor' ? 'investor' : selectedEntity?.value === 'founder' ? 'founder' : undefined,
      investor_type: selectedEntity?.value === 'investor' ? selectedInvestorType?.value : undefined,
      status: selectedInvoiceStatus?.value ?? undefined,
      less_than: selectedLessThan ?? undefined,
      greater_than: selectedGreaterThan ?? undefined,
    });

    // Pass everything in; the debounced function “owns” the timer now.
    loadFilteredUsers(selectedEntity?.value ?? '', page, filter ?? '', extraFilters);
  }, [
    loadFilteredUsers,
    selectedEntity,
    page,
    filter,
    selectedResults.companies,
    selectedResults.deals,
    selectedDealStage,
    selectedInvestorType,
    selectedInvoiceStatus,
    selectedLessThan,
    selectedGreaterThan,
  ]);

  useEffect(() => {
    return () => {
      loadFilteredUsers.cancel();
    };
  }, [loadFilteredUsers]);

  const entityTypeForMembers = useMemo(() => {
    if (selectedEntity?.value === 'investor' || selectedEntity?.value === 'founder') {
      return 'company';
    }
    return selectedEntity?.value ?? '';
  }, [selectedEntity]);

  useEffect(() => {
    if (!selectedEntity?.value) return;

    const filters = selectedEntity.value === 'company' && selectedCountry ? { country: selectedCountry.value } : {};

    const extraFilters = getUserFiltersQuery(filters);
    setEntityMembersFilters(extraFilters);
    setEntityMembersAreLoading(true);

    dispatch(
      getCustomMailEntityMembers({
        page: 1,
        per_page: 20,
        entity: entityTypeForMembers,
        filter: extraFilters,
      }),
    )
      .unwrap()
      .finally(() => setEntityMembersAreLoading(false));
  }, [dispatch, entityTypeForMembers, selectedEntity?.value, selectedCountry]);

  const handleInputChange = debounce((newValue: string) => {
    setEntityMembersAreLoading(true);

    dispatch(
      getCustomMailEntityMembers({
        page: 1,
        per_page: 20,
        entity: selectedEntity?.value ?? '',
        filter: `${entityMembersFilters}`.replace(/&$/, ''),
        query: newValue || undefined,
      }),
    )
      .unwrap()
      .finally(() => {
        setEntityMembersAreLoading(false);
      });
  }, 1500);

  const handleUserInputChange = debounce((newValue: string) => {
    setUsersQuery(newValue);
    setUsersAreLoading(true);
    dispatch(
      getCustomMailUsers({
        page: 1,
        per_page: 20,
        entity: selectedEntity?.value ?? '',
        filter: `${usersFilters}`,
        ...(newValue && { query: newValue }),
      }),
    )
      .unwrap()
      .finally(() => {
        setUsersAreLoading(false);
      });
  }, 1500);

  const entityOptions: Option[] = entities.options
    .filter((option) => option.key && option.label)
    .map((option) => ({
      value: option.key as string,
      label: option.label,
      key: option.key,
    }));

  const entityMemberOptions = useMemo(() => {
    return entityMembers?.map((entityMember) => ({
      label: entityMember.name,
      value: entityMember.id.toString(),
      id: entityMember.id.toString(),
    }));
  }, [entityMembers]);

  const usersOptions = useMemo(() => {
    return usersList?.map((user) => ({
      label: user.name,
      value: user.id.toString(),
      id: user.id.toString(),
    }));
  }, [usersList]);

  const countryOptions = useMemo(() => {
    return countryList?.map((country) => ({
      label: country.name,
      value: country.name,
    }));
  }, [countryList]);

  const formattedPreview = useMemo(() => {
    if (!template) return '';
    return fixedPreview?.replace('{{content}}', template);
  }, [template, fixedPreview]);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = template;
    }
  }, [shouldBeCleared]);

  useEffect(() => {
    setValue('entityMember', null);
    setValue('investorType', null);
    setValue('country', null);
    setValue('dealStage', null);
    setValue('invoiceStatus', null);
    setValue('lessThan', null);
    setValue('greaterThan', null);

    setSelectedResults((prev) => ({
      deals: [],
      companies: [],
      users: prev.users, // Keep the existing users
    }));
  }, [selectedEntity, setValue]);

  const handleClear = () => {
    reset({
      entity: null,
      entityMember: null,
      users: null,
    });
    setSelectedResults({
      deals: [],
      companies: [],
      users: [],
    });
    setShouldBeCleared(!shouldBeCleared);
    setTemplate(content);
  };

  const sendEmail = () => {
    onOpen();
  };
  const resetFilters = (resetKey?: string) => {
    setValue(resetKey as any, null);
  };

  const fetchAllUsersCallback = async (): Promise<Option[]> => {
    const response = await dispatch(
      getCustomMailUsers({
        page: 1,
        per_page: 9999,
        entity: selectedEntity?.value ?? '',
        filter: `${usersFilters}`.replace(/&$/, ''),
        ...(usersQuery && { query: usersQuery }),
      }),
    ).unwrap();

    return response.data.map((user) => ({
      label: user.name,
      value: user.id.toString(),
      id: user.id.toString(),
    }));
  };

  return (
    <>
      <MailingConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        recipients={selectedResults.users.map((user) => user.name)}
        ids={selectedResults.users.map((user) => user.id)}
        clearSelectedResults={handleClear}
        entity={selectedEntity?.value}
        subject={setSubject}
        content={template}
      />
      <form className='w-full mt-8' onSubmit={handleSubmit(sendEmail)}>
        <div className='w-full flex flex-col md:gap-4 md:flex-row'>
          <FormContent
            fields={CREATE_CUSTOM_MAIL_FORM_FIELDS(entityOptions)}
            register={register}
            control={control}
            errors={errors}
          />

          {selectedEntity?.value === 'deal' && (
            <SelectFormInput
              label='Deal Stage'
              placeholder='Deal Stage'
              control={control as unknown as Control}
              name='dealStage'
              options={SHARE_EXCHANGE_STAGES_OPTIONS}
              onClearCallback={() => resetFilters('dealStage')}
              isClearable
              className='mb-4'
            />
          )}

          {selectedEntity?.value === 'investor' && (
            <SelectFormInput
              label='Investor Type'
              placeholder='Investor Type'
              control={control as unknown as Control}
              name='investorType'
              options={INVESTORS_TYPE_OPTIONS}
              onClearCallback={() => resetFilters('investorType')}
              isClearable
              className='mb-4'
            />
          )}

          {selectedEntity?.value === 'invoices' && (
            <SelectFormInput
              label='Invoice Status'
              placeholder='Invoice Status'
              control={control as unknown as Control}
              name='invoiceStatus'
              options={INVOICE_STATUS_OPTIONS}
              onClearCallback={() => resetFilters('invoiceStatus')}
              isClearable
              className='mb-4'
            />
          )}

          {selectedEntity?.value === 'company' && (
            <SelectFormInput
              label='Country'
              placeholder='Country'
              control={control as unknown as Control}
              name='country'
              options={countryOptions}
              onClearCallback={() => resetFilters('country')}
              isClearable
              className='mb-4'
            />
          )}
        </div>

        {selectedEntity?.value === 'invoices' && (
          <div className='w-full flex flex-col md:gap-4 md:flex-row'>
            <Input
              {...register('greaterThan')}
              name='greaterThan'
              placeholder='Amount greater than'
              label='Amount greater than'
              labelClassName='labelClassName'
              className='mb-4'
              type={FieldTypes.NUMBER}
            />
            <Input
              {...register('lessThan')}
              name='lessThan'
              placeholder='Amount less than'
              label='Amount less than'
              labelClassName='labelClassName'
              className='mb-4'
              type={FieldTypes.NUMBER}
            />
          </div>
        )}

        {selectedEntity && selectedEntity.value !== 'invoices' && (
          // For "deal" and "company" use CreatableSelectFormInput
          <CreatableSelectFormInput
            closeMenu
            required
            className='col-span-full md:col-span-1 mb-4'
            label={inputLabel}
            placeholder={`Select ${inputLabel}`}
            control={control as unknown as Control}
            name='entityMember'
            options={entityMemberOptions}
            isLoading={entityMembersAreLoading}
            onInputChange={(newValue, actionMeta) => {
              if (actionMeta.action === 'input-change') {
                handleInputChange(newValue);
              }
            }}
            onClearCallback={() => {
              setSelectedResults((prev) => ({
                ...prev,
                deals: [],
                companies: [],
                users: prev.users,
              }));
            }}
            isMulti
            creatableOff
            isAbsoluteError
            validation={{ required: `Please select at least one ${inputLabel}` }}
          />
        )}

        {selectedEntity && (
          <ExternalMultiSelect
            className='col-span-full md:col-span-1 mb-4'
            disabled={
              selectedResults.deals.length === 0 &&
              selectedResults.companies.length === 0 &&
              selectedEntity.value !== 'invoices'
            }
            placeholder='Select Recipients'
            closeMenu
            control={control as unknown as Control}
            name='users'
            options={usersOptions || []}
            label='Recipients'
            onInputChange={(newValue, actionMeta) => {
              if (actionMeta.action === 'input-change') {
                handleUserInputChange(newValue);
              }
            }}
            fetchAllCallback={fetchAllUsersCallback}
            validation={{ required: 'Please select at least one recipient' }}
            isLoading={usersAreLoading}
          />
        )}

        <div>
          <Input
            {...register('subject')}
            name='subject'
            placeholder='Subject'
            label='Subject'
            className='mb-4 mt-8'
            inputClassName='bg-violet-100'
            type={FieldTypes.TEXT}
          />
        </div>

        {/* Preview / Editor sections */}
        <SnippetLayout className={SHARED_TEMPLATE_CLASSNAMES}>
          <div
            ref={editorRef}
            className='p-4'
            dangerouslySetInnerHTML={{ __html: content }}
            contentEditable
            onInput={(e: ChangeEvent<HTMLDivElement>) => setTemplate(e.target.innerHTML)}
          />
        </SnippetLayout>
        <SnippetLayout className={cn(SHARED_TEMPLATE_CLASSNAMES, 'h-max')}>
          <div className='w-fit mt-6 mx-auto p-4' dangerouslySetInnerHTML={{ __html: formattedPreview }} />
        </SnippetLayout>

        <div className='flex justify-end mt-6'>
          <ButtonWithTooltip
            type='submit'
            variant={ButtonVariants.PRIMARY}
            onClick={sendEmail}
            disabled={selectedResults.users.length === 0}
            disabledTooltip={Boolean(selectedResults.users.length > 0)}
            anchorId='send-investor-invite'
            tooltipContent={'Please select at least one recipient'}
            wrapperClassName='w-full xs:w-auto'
            place='top-start'
            offset={20}
          >
            Send email
          </ButtonWithTooltip>
        </div>
      </form>
    </>
  );
};
export default MailingForm;
